import React, { useEffect, useRef, useState } from 'react';

// ══════════════════════════════════════════════
// LocationGoogleMap — Google Maps for Location section
// Uses same pattern as HRGoogleMap in HRLocation.jsx
//
// ملاحظة TS: مفيش @types/google.maps متثبتة في المشروع، فكل استخدام لـ
// window.google بيتعامل معه كـ any (زي باقي الـ globals الخارجية التانية
// اللي اتحولت قبل كده زي window.XLSX/window.jspdf).
// ══════════════════════════════════════════════
interface ProjectForMap {
    id?: string | number;
    name?: string;
    latitude?: number;
    longitude?: number;
}

interface TaskForMap {
    latitude?: number | null;
    longitude?: number | null;
    sub_task?: string;
    system?: string;
    status?: string;
}

interface LocationGoogleMapProps {
    project: ProjectForMap;
    tasks: TaskForMap[];
    darkMode?: boolean;
    onMapClick?: (lat: number, lng: number) => void;
}

function LocationGoogleMap({ project, tasks, onMapClick }: LocationGoogleMapProps) {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstance = useRef<any>(null);
    const markersRef = useRef<any[]>([]);
    // ✅ بيحمل أحدث نسخة من onMapClick عشان الـ click listener (اللي بيتسجّل
    // مرة واحدة بس وقت إنشاء الخريطة) ما يفضلش شايل نسخة قديمة (stale closure)
    // لو الأب بعت onMapClick تاني بعد أول render.
    const onMapClickRef = useRef(onMapClick);
    const prevProjectIdRef = useRef(project?.id);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        onMapClickRef.current = onMapClick;
    }, [onMapClick]);

    // ── Init map ──
    useEffect(() => {
        if (!mapRef.current) return;
        let destroyed = false;

        const initMap = async () => {
            try {
                await (window as any).google.maps.importLibrary("marker");
                if (destroyed) return;

                const map = new (window as any).google.maps.Map(mapRef.current, {
                    center: { lat: project.latitude, lng: project.longitude },
                    zoom: 16,
                    mapTypeId: "hybrid",
                    mapId: "location_section_map",
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: true,
                    zoomControl: true,
                    styles: [
                        { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }
                    ],
                });

                // Expose map globally for external controls
                (window as any).__locationMap = map;
                mapInstance.current = map;

                // Click handler — pin task
                map.addListener("click", (e: any) => {
                    if (onMapClickRef.current) onMapClickRef.current(e.latLng.lat(), e.latLng.lng());
                });

                if (!destroyed) setReady(true);
            } catch (err) {
                console.error("LocationGoogleMap init error:", err);
            }
        };

        if ((window as any).google?.maps) {
            initMap();
        } else {
            const checkInterval = setInterval(() => {
                if ((window as any).google?.maps) {
                    clearInterval(checkInterval);
                    initMap();
                }
            }, 200);
            return () => clearInterval(checkInterval);
        }

        return () => {
            destroyed = true;
            markersRef.current.forEach(m => { try { m.map = null; } catch (_) { } });
            markersRef.current = [];
            (window as any).__locationMap = null;
            mapInstance.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- الخريطة بتتعمل مرة واحدة بس عند mount بمركز project.latitude/longitude وقتها؛ onMapClick بقى بيتقرأ من onMapClickRef عشان يتجنّب stale closure، وإعادة توسيط الخريطة لما المشروع يتغيّر بقت مسؤولية الـ effect التاني (رسم الـ markers)
    }, []);

    // ── Draw markers ──
    useEffect(() => {
        if (!ready || !mapInstance.current) return;

        const map = mapInstance.current;
        const gm = (window as any).google.maps;

        // ✅ إعادة توسيط الخريطة لما المشروع نفسه يتغيّر (مثلاً بعد تبديل مشروع
        // من الـ selector) — من غير ما نعيد التوسيط في كل مرة الـ tasks بس
        // بتتحدّث (زي الـ auto-refresh)، عشان ما نبوّظش على المستخدم لو كان
        // مكبّر/مزحلق على مكان تاني في الخريطة.
        if (prevProjectIdRef.current !== project?.id) {
            prevProjectIdRef.current = project?.id;
            if (project?.latitude && project?.longitude) {
                map.setCenter({ lat: project.latitude, lng: project.longitude });
            }
        }

        // Clear old markers
        markersRef.current.forEach(m => { try { m.map = null; } catch (_) { } });
        markersRef.current = [];

        const AdvancedMarkerElement = gm.marker?.AdvancedMarkerElement;

        // ── Project marker ──
        const projPin = document.createElement("div");
        projPin.innerHTML = `
            <div style="
                background:linear-gradient(135deg,#3b82f6,#6366f1);
                color:#fff;padding:8px 12px;border-radius:12px;
                font-size:12px;font-weight:900;
                box-shadow:0 4px 16px rgba(59,130,246,0.5);
                border:2px solid rgba(255,255,255,0.3);
                white-space:nowrap;
                display:flex;align-items:center;gap:6px;
            ">
                📍 ${project.name?.slice(0, 20) || 'Project'}
                <div style="
                    position:absolute;bottom:-8px;left:50%;transform:translateX(-50%);
                    width:0;height:0;
                    border-left:8px solid transparent;
                    border-right:8px solid transparent;
                    border-top:8px solid #3b82f6;
                "></div>
            </div>
        `;

        let projMarker;
        if (AdvancedMarkerElement) {
            projMarker = new AdvancedMarkerElement({
                position: { lat: project.latitude, lng: project.longitude },
                map,
                title: project.name,
                content: projPin,
            });
        } else {
            projMarker = new gm.Marker({
                position: { lat: project.latitude, lng: project.longitude },
                map,
                title: project.name,
                icon: {
                    path: gm.SymbolPath.CIRCLE,
                    scale: 14,
                    fillColor: "#3b82f6",
                    fillOpacity: 1,
                    strokeColor: "#fff",
                    strokeWeight: 3,
                },
            });
        }
        markersRef.current.push(projMarker);

        // ── Task markers ──
        tasks.filter(t => t.latitude && t.longitude).forEach(task => {
            const color = task.status === 'Completed' ? '#10b981' : task.status === 'In Progress' ? '#3b82f6' : '#94a3b8';

            const taskPin = document.createElement("div");
            taskPin.innerHTML = `
                <div style="
                    background:${color};
                    width:14px;height:14px;border-radius:50%;
                    border:2.5px solid #fff;
                    box-shadow:0 2px 8px ${color}88;
                    cursor:pointer;
                    position:relative;
                "></div>
            `;

            let taskMarker: any;
            const iw = new gm.InfoWindow({
                content: `
                    <div style="font-family:-apple-system,sans-serif;min-width:200px">
                        <div style="background:linear-gradient(135deg,#1e293b,#334155);padding:12px 14px;border-radius:8px 8px 0 0">
                            <p style="margin:0;color:#fff;font-weight:800;font-size:13px">${task.sub_task || 'Task'}</p>
                            <p style="margin:4px 0 0;color:#94a3b8;font-size:10px">${task.system || ''}</p>
                        </div>
                        <div style="padding:10px 14px;background:#fff;border-radius:0 0 8px 8px">
                            <span style="
                                background:${color}18;color:${color};
                                font-size:10px;font-weight:800;
                                padding:3px 8px;border-radius:6px;
                                border:1px solid ${color}30;
                            ">${task.status || 'Pending'}</span>
                        </div>
                    </div>
                `,
                maxWidth: 240,
            });

            if (AdvancedMarkerElement) {
                taskMarker = new AdvancedMarkerElement({
                    position: { lat: task.latitude, lng: task.longitude },
                    map,
                    title: task.sub_task,
                    content: taskPin,
                });
                taskMarker.addListener("click", () => {
                    iw.open({ anchor: taskMarker, map });
                });
            } else {
                taskMarker = new gm.Marker({
                    position: { lat: task.latitude, lng: task.longitude },
                    map,
                    title: task.sub_task,
                    icon: {
                        path: gm.SymbolPath.CIRCLE,
                        scale: 7,
                        fillColor: color,
                        fillOpacity: 1,
                        strokeColor: "#fff",
                        strokeWeight: 2,
                    },
                });
                taskMarker.addListener("click", () => {
                    iw.open(map, taskMarker);
                });
            }

            markersRef.current.push(taskMarker);
        });

    }, [ready, tasks, project]);

    return (
        <div ref={mapRef} style={{ width: '100%', height: '100%', minHeight: 480 }} />
    );
}

export default LocationGoogleMap;
